// Slide-based lesson content types
import type React from 'react';
import type { CompositionBlock, CompositionScoring } from '../../../lib/scenario-builder/composition/types';
import type { Board } from '../../../lib/marking/board';
import type { ReviseRun } from '../../../lib/revise/types';

export type SlideTemplate =
  | 'text'             // Unified text slide with layout variants
  | 'cover'            // Course cover/title page with logo + accent
  | 'image'            // Full-bleed image
  | 'video'            // Full-screen video embed
  | 'quiz'             // Interactive quiz
  | 'activity'         // Interactive activity (likert, survey-choice, free-text, dropdown, ranking, matching)
  | 'webapp'           // Embedded webapp (seamless)
  | 'code'             // Code with syntax highlighting
  | 'canva'            // Embedded Canva slideshow
  | 'attune'           // Personalised AI learning moment (hook / facilitator / experience)
  | 'reflection'       // Open-ended reflection prompts
  // Scenario builder templates (kula-create port — KULA_BUILDER_INTEGRATION.md Phase 1)
  | 'reveal-predict'
  | 'scenario-simulation'
  | 'spot-the-error'
  | 'drag-sort'
  | 'gallery-walk'
  | 'timeline-navigator'
  | 'confidence-meter'
  | 'progressive-builder'
  | 'guided-sandbox'
  | 'scaffolded-investigation'
  | 'perspective-lenses'
  | 'tension-mapper'
  | 'before-after-bridge'
  | 'claim-evidence'
  | 'layered-reveal'
  | 'reflect-act'
  | 'teach-back' // Learner teaches a struggling student: diagnose the belief, coach, catch the residue (CREATE-COMPOSITION-HANDOVER.md §9)
  | 'composed-experience' // Kula Create Workshop: bespoke experience assembled from capturing primitive blocks (CREATE-COMPOSITION-HANDOVER.md §3)
  | 'hotspot-explorer' // Image with clickable zones (Dylan's 17th type — restored from original Kula Create)
  | 'source-examination' // Exam-style revision: one source + scaffolded L1/L2/L3 questions (REVISION_TEMPLATE_PLAN.md)
  | 'mark-this-answer' // Revision companion: evaluate sample answers against a rubric (REVISION_TEMPLATE_PLAN.md)
  | 'revision-exam' // The single self-contained revision interactive — whole paper in one slide (REVISION_TEMPLATE_PLAN.md)
  | 'essay-marking' // Learner writes a CAPS History essay; the marking engine reads it (KULA_INTEGRATION_SPEC Piece D)
  | 'bridge'           // Auto-inserted narrative bridge between scenario-group slides
  | 'interactive-slot' // Placeholder unit awaiting Create (Phase C — Builder redesign)
  | 'generated-interactive' // Activity Door build — sealed self-contained HTML (KULA-ACTIVITY-DOORS-TOP10.md, fork B)
  | 'revise-run'       // Kula Revise lab: one lesson / check / paper / skeleton played end to end (KULA-REVISE-LAB-ENGINE-PLAN.md)
  // Deprecated — kept for backward compat parsing, migrated at load time
  | 'media'            // → migrated to 'image' or 'video'
  | 'title' | 'content' | 'split' | 'bullets' | 'image-text' | 'text-block';

export type TextSlideLayout =
  | 'title'        // Big headline + subtitle
  | 'body'         // Heading + body text
  | 'bullets'      // Heading + bulleted body (pre-filled with <ul>)
  | 'two-column'   // Two rich text columns side by side
  | 'image-text';  // Image alongside heading + body

export type ActivityType =
  | 'likert'          // 1-5/7 agreement scale (survey only)
  | 'survey-choice'   // Multiple choice, no correct answer (survey only)
  | 'free-text'       // Open text response (survey only)
  | 'dropdown'        // Select from dropdown list (survey or quiz)
  | 'ranking'         // Drag to reorder items (survey or quiz)
  | 'matching';       // Click-to-connect pairs (quiz only)

export type ActivityMode = 'survey' | 'quiz';

export type ThemeId =
  | 'modern-light'
  | 'modern-dark'
  | 'warm'
  | 'ocean'
  | 'tenant-brand'
  // Kula-create visual presets (Phase 1 port — KULA_BUILDER_INTEGRATION.md §G).
  // The Designer Concepts agent's `recommendedStyle` field maps to one of these.
  | 'warm-playful'
  | 'editorial'
  | 'bold-modern'
  | 'calm-cinematic'
  // Emotive "theme worlds" (design exploration → vertical slices). See
  // THEME_WORLDS_PLAN.md. Bespoke renderers exist for each world's flagship
  // component; other templates fall back to standard themed render.
  | 'neon-oracle'   // futuristic sci-fi terminal/oracle — flagship: scenario-simulation
  | 'obsidian'      // neo-noir case file — flagship: claim-evidence
  | 'aurora'        // liquid glass + aurora — flagship: confidence-meter
  | 'terra'         // sunlit greenhouse / botanical study — flagship: progressive-builder
  | 'hyperpop'      // neo-brutalist candy arcade — flagship: reveal-predict
  | 'risograph'     // spot-ink zine print — flagship: perspective-lenses
  | 'lamplight';    // mentor's night desk under a warm lamp — flagship: teach-back

// Tenant-defined brand slide theme. Persisted inside tenants.branding.slideTheme.
// Colours are provided per mode; the font family is shared across light and dark.
export interface BrandPalette {
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  primary: string;
  accent: string;
}

export interface BrandSlideTheme {
  /** Label shown on the theme card (e.g. "Mint brand") */
  name: string;
  /** Id from BRAND_FONTS (see lib/googleFonts) — resolved to a CSS stack at render time */
  fontFamily: string;
  variants: {
    light: BrandPalette;
    dark: BrandPalette;
  };
}

export interface TypographyScale {
  /** @deprecated Use var(--slide-font-{level}) via slideTypographyStyle() instead */
  size: string;
  ratio: number;        // multiplier relative to body=1.0
  weight: number;
  lineHeight: number;
  letterSpacing: string;
}

export type TypographyLevel = 'title' | 'heading' | 'subheading' | 'body' | 'caption';

export type ThemeMode = 'light' | 'dark';

export interface Theme {
  id: ThemeId;
  name: string;
  mode: ThemeMode;
  colors: {
    background: string;
    surface: string;
    text: string;
    textMuted: string;
    primary: string;
    accent: string;
    feedback: {
      correctBg: string;
      correctBorder: string;
      correctText: string;
      warningBg: string;
      warningBorder: string;
      warningText: string;
      incorrectBg: string;
      incorrectBorder: string;
      incorrectText: string;
    };
  };
  fonts: {
    heading: string;
    body: string;
    code: string;
  };
  typography: {
    /** Single scaling anchor — all levels derive from this via CSS calc() */
    baseFont: string;
    title: TypographyScale;
    heading: TypographyScale;
    subheading: TypographyScale;
    body: TypographyScale;
    caption: TypographyScale;
  };
  spacing: {
    /** Roomy slides: title, quiz, bullets, text-block */
    padded: string;
    /** Dense content: content, code, split columns */
    compact: string;
    /** Horizontal-only inset for text columns in multi-column layouts */
    insetX: string;
    /** Gap between heading, accent bar, and body */
    inner: string;
    /** @deprecated alias of padded — kept for backward compat */
    slide: string;
  };
}

const lightFeedback = {
  correctBg: '#f0fdf4',
  correctBorder: '#22c55e',
  correctText: '#166534',
  warningBg: '#fffbeb',
  warningBorder: '#f59e0b',
  warningText: '#92400e',
  incorrectBg: '#fef2f2',
  incorrectBorder: '#ef4444',
  incorrectText: '#991b1b',
};

const darkFeedback = {
  correctBg: '#052e1a',
  correctBorder: '#22c55e',
  correctText: '#86efac',
  warningBg: '#451a03',
  warningBorder: '#f59e0b',
  warningText: '#fcd34d',
  incorrectBg: '#3f0a0a',
  incorrectBorder: '#ef4444',
  incorrectText: '#fca5a5',
};

export const themes: Record<ThemeId, Theme> = {
  'modern-light': {
    id: 'modern-light',
    name: 'Modern Light',
    mode: 'light',
    colors: {
      background: '#ffffff',
      surface: '#f8fafc',
      text: '#0f172a',
      textMuted: '#64748b',
      primary: '#3b82f6',
      accent: '#8b5cf6',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"DM Serif Display", Georgia, serif',
      body: '"DM Sans", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5cqi, 4rem)',   ratio: 3.33, weight: 400, lineHeight: 1.15, letterSpacing: '-0.01em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.5rem)',  ratio: 2.0,  weight: 400, lineHeight: 1.2,  letterSpacing: '-0.005em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',  ratio: 1.33, weight: 500, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.25rem)', ratio: 1.0,  weight: 400, lineHeight: 1.8,  letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)', ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.01em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'modern-dark': {
    id: 'modern-dark',
    name: 'Modern Dark',
    mode: 'dark',
    colors: {
      background: '#000000',
      surface: '#111111',
      text: '#f8fafc',
      textMuted: '#94a3b8',
      primary: '#bf1717',
      accent: '#F56042',
      feedback: darkFeedback,
    },
    fonts: {
      heading: '"Inter", system-ui, sans-serif',
      body: '"Inter", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5cqi, 3.75rem)', ratio: 3.33, weight: 700, lineHeight: 1.1,  letterSpacing: '-0.02em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.25rem)',  ratio: 2.0,  weight: 600, lineHeight: 1.2,  letterSpacing: '-0.015em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',   ratio: 1.33, weight: 500, lineHeight: 1.4,  letterSpacing: '-0.005em' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.2rem)',   ratio: 1.0,  weight: 400, lineHeight: 1.75, letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)',  ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.01em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'warm': {
    id: 'warm',
    name: 'Warm',
    mode: 'light',
    colors: {
      background: '#fffbeb',
      surface: '#fef3c7',
      text: '#451a03',
      textMuted: '#92400e',
      primary: '#f59e0b',
      accent: '#ef4444',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Fraunces", Georgia, serif',
      body: '"Source Sans 3", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2rem, 5cqi, 3.75rem)',   ratio: 3.33, weight: 600, lineHeight: 1.15, letterSpacing: '-0.01em' },
      heading:    { size: 'clamp(1.4rem, 3cqi, 2.25rem)',  ratio: 2.0,  weight: 600, lineHeight: 1.25, letterSpacing: '-0.005em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',   ratio: 1.33, weight: 600, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.2rem)',   ratio: 1.0,  weight: 400, lineHeight: 1.8,  letterSpacing: '0.005em' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)',  ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.01em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'ocean': {
    id: 'ocean',
    name: 'Ocean',
    mode: 'light',
    colors: {
      background: '#ecfeff',
      surface: '#cffafe',
      text: '#164e63',
      textMuted: '#0e7490',
      primary: '#06b6d4',
      accent: '#0891b2',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Playfair Display", Georgia, serif',
      body: '"Outfit", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5cqi, 4rem)',    ratio: 3.33, weight: 700, lineHeight: 1.15, letterSpacing: '-0.01em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.5rem)',   ratio: 2.0,  weight: 600, lineHeight: 1.2,  letterSpacing: '-0.005em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',   ratio: 1.33, weight: 500, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.2rem)',   ratio: 1.0,  weight: 400, lineHeight: 1.8,  letterSpacing: '0.01em' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)',  ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.02em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  // Placeholder for tenant brand themes. Callers with access to the tenant
  // should resolve the real theme via resolveTheme() / getSlideTheme() — this
  // entry exists so `themes[id]` lookups don't crash when a tenant theme is
  // selected but no branding is available (e.g. exports, offline tools).
  'tenant-brand': {
    id: 'tenant-brand',
    name: 'Brand',
    mode: 'light',
    colors: {
      background: '#ffffff',
      surface: '#f8fafc',
      text: '#0f172a',
      textMuted: '#64748b',
      primary: '#3b82f6',
      accent: '#8b5cf6',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Inter", system-ui, sans-serif',
      body: '"Inter", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5cqi, 4rem)',   ratio: 3.33, weight: 600, lineHeight: 1.15, letterSpacing: '-0.01em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.5rem)',  ratio: 2.0,  weight: 600, lineHeight: 1.2,  letterSpacing: '-0.005em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',  ratio: 1.33, weight: 500, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.25rem)', ratio: 1.0,  weight: 400, lineHeight: 1.8,  letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)', ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.01em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  // ---- Kula-create visual presets (ported from kula-create/lib/presets.ts) ----
  'warm-playful': {
    id: 'warm-playful',
    name: 'Warm & Playful',
    mode: 'light',
    colors: {
      background: '#faf7f2',
      surface: '#ffffff',
      text: '#2d2a26',
      textMuted: '#6b6560',
      primary: '#e8734a',
      accent: '#e8734a',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Plus Jakarta Sans", system-ui, sans-serif',
      body: '"Plus Jakarta Sans", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5cqi, 4rem)',   ratio: 3.33, weight: 700, lineHeight: 1.1,  letterSpacing: '-0.015em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.5rem)',  ratio: 2.0,  weight: 600, lineHeight: 1.2,  letterSpacing: '-0.005em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',  ratio: 1.33, weight: 600, lineHeight: 1.35, letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.2rem)',  ratio: 1.0,  weight: 400, lineHeight: 1.7,  letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)', ratio: 0.67, weight: 500, lineHeight: 1.45, letterSpacing: '0.01em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'editorial': {
    id: 'editorial',
    name: 'Editorial',
    mode: 'light',
    colors: {
      background: '#f5f3ee',
      surface: '#ffffff',
      text: '#1a1a1a',
      textMuted: '#555555',
      primary: '#b8423a',
      accent: '#b8423a',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Fraunces", Georgia, serif',
      body: '"Work Sans", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5cqi, 4rem)',   ratio: 3.33, weight: 600, lineHeight: 1.05, letterSpacing: '-0.02em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.5rem)',  ratio: 2.0,  weight: 600, lineHeight: 1.15, letterSpacing: '-0.01em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',  ratio: 1.33, weight: 500, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.25rem)', ratio: 1.0,  weight: 400, lineHeight: 1.75, letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)', ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.02em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'bold-modern': {
    id: 'bold-modern',
    name: 'Bold & Modern',
    mode: 'light',
    colors: {
      background: '#ffffff',
      surface: '#fafafa',
      text: '#0a0a0a',
      textMuted: '#525252',
      primary: '#0066ff',
      accent: '#0066ff',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Syne", system-ui, sans-serif',
      body: '"Space Grotesk", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5cqi, 4rem)',   ratio: 3.33, weight: 700, lineHeight: 1.0,  letterSpacing: '-0.02em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.5rem)',  ratio: 2.0,  weight: 700, lineHeight: 1.15, letterSpacing: '-0.015em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',  ratio: 1.33, weight: 600, lineHeight: 1.35, letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.2rem)',  ratio: 1.0,  weight: 400, lineHeight: 1.65, letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)', ratio: 0.67, weight: 500, lineHeight: 1.5,  letterSpacing: '0.04em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'neon-oracle': {
    id: 'neon-oracle',
    name: 'Neon Oracle',
    mode: 'dark',
    colors: {
      background: '#05060a',
      surface: '#0b0f17',
      text: '#e8eef7',
      // 6.78:1 on the void, 6.42:1 on the surface. The previous #6b7689 was
      // 4.41:1 — under AA at the 10-11px tracked-out sizes this world uses it
      // for almost every label. Nothing here should dim it further with alpha.
      textMuted: '#8b96a8',
      primary: '#c6ff3f',
      accent: '#22e3ff',
      feedback: darkFeedback,
    },
    // Space Mono for display (a terminal face with REAL capitals) + IBM Plex
    // Sans for prose + IBM Plex Mono for terminal chrome and readouts.
    // Was: Major Mono Display / IBM Plex Mono body. Major Mono Display has no
    // case distinction (cap-height 71 vs x-height 70 at 100px), so sentence-case
    // titles rendered with visually mismatched forms and the A/B/C choice keys
    // came out as unreadable geometric marks. The mono BODY also made this the
    // only theme of sixteen that set long-form prose in monospace; mono now
    // belongs to `code`, which the renderers use for chrome only.
    fonts: {
      heading: '"Space Mono", "IBM Plex Mono", monospace',
      body: '"IBM Plex Sans", system-ui, sans-serif',
      code: '"IBM Plex Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2rem, 5cqi, 3.4rem)',    ratio: 3.33, weight: 700, lineHeight: 1.1,  letterSpacing: '0.01em' },
      heading:    { size: 'clamp(1.4rem, 3cqi, 2.2rem)',   ratio: 2.0,  weight: 700, lineHeight: 1.2,  letterSpacing: '0.02em' },
      subheading: { size: 'clamp(1.05rem, 2cqi, 1.4rem)',  ratio: 1.33, weight: 500, lineHeight: 1.4,  letterSpacing: '0.01em' },
      body:       { size: 'clamp(0.95rem, 1.5cqi, 1.1rem)',ratio: 1.0,  weight: 400, lineHeight: 1.7,  letterSpacing: '0' },
      caption:    { size: 'clamp(0.75rem, 1cqi, 0.9rem)',  ratio: 0.67, weight: 500, lineHeight: 1.5,  letterSpacing: '0.08em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'aurora': {
    id: 'aurora',
    name: 'Aurora Drift',
    mode: 'dark',
    colors: {
      background: '#0a0e1f',
      surface: '#141e3c',
      text: '#eaf4ff',
      textMuted: '#9fb3d9',
      primary: '#5ef2a0',
      accent: '#8b6cff',
      feedback: darkFeedback,
    },
    fonts: {
      heading: '"Instrument Serif", Georgia, serif',
      body: '"Hanken Grotesk", system-ui, sans-serif',
      code: '"IBM Plex Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.4rem, 5.6cqi, 4.4rem)', ratio: 3.33, weight: 400, lineHeight: 1.05, letterSpacing: '0' },
      heading:    { size: 'clamp(1.7rem, 3.2cqi, 2.8rem)', ratio: 2.0,  weight: 400, lineHeight: 1.12, letterSpacing: '0' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',   ratio: 1.33, weight: 500, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.2rem)',   ratio: 1.0,  weight: 400, lineHeight: 1.7,  letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)',  ratio: 0.67, weight: 500, lineHeight: 1.5,  letterSpacing: '0.04em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'terra': {
    id: 'terra',
    name: 'Terra',
    mode: 'light',
    colors: {
      background: '#f3e9d2',
      surface: '#fffaf0',
      text: '#3a2c18',
      textMuted: '#7a5d39',
      primary: '#2f6e3b',
      accent: '#d2613c',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Gloock", "Fraunces", Georgia, serif',
      body: '"Hanken Grotesk", system-ui, sans-serif',
      code: '"Courier Prime", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.5rem, 6cqi, 4.6rem)',   ratio: 3.33, weight: 400, lineHeight: 0.98, letterSpacing: '-0.01em' },
      heading:    { size: 'clamp(1.6rem, 3.2cqi, 2.6rem)', ratio: 2.0,  weight: 400, lineHeight: 1.1,  letterSpacing: '0' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',   ratio: 1.33, weight: 600, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.18rem)',  ratio: 1.0,  weight: 400, lineHeight: 1.62, letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)',  ratio: 0.67, weight: 600, lineHeight: 1.5,  letterSpacing: '0.06em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'risograph': {
    id: 'risograph',
    name: 'Risograph Assembly',
    mode: 'light',
    colors: {
      background: '#f4efe2',
      surface: '#faf6ea',
      text: '#16130d',
      textMuted: '#6b6456',
      primary: '#2453ff',
      accent: '#ff48a0',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Anton", "Archivo Black", sans-serif',
      body: '"Newsreader", Georgia, serif',
      code: '"Space Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.4rem, 7vw, 5.1rem)',   ratio: 3.33, weight: 400, lineHeight: 0.92, letterSpacing: '0.5px' },
      heading:    { size: 'clamp(1.8rem, 5vw, 3.2rem)',   ratio: 2.0,  weight: 400, lineHeight: 0.96, letterSpacing: '0.5px' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.4rem)',  ratio: 1.33, weight: 400, lineHeight: 1.3,  letterSpacing: '1px' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.18rem)', ratio: 1.0,  weight: 400, lineHeight: 1.5,  letterSpacing: '0' },
      caption:    { size: 'clamp(0.78rem, 1cqi, 0.9rem)', ratio: 0.67, weight: 400, lineHeight: 1.4,  letterSpacing: '1.5px' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'hyperpop': {
    id: 'hyperpop',
    name: 'Hyperpop Arcade',
    mode: 'light',
    colors: {
      background: '#fdfcf7',
      surface: '#ffffff',
      text: '#0a0a0a',
      textMuted: '#5b5648',
      primary: '#2d5cff',
      accent: '#ff3d9a',
      feedback: lightFeedback,
    },
    fonts: {
      heading: '"Bricolage Grotesque", "Hanken Grotesk", sans-serif',
      body: '"Hanken Grotesk", system-ui, sans-serif',
      code: '"Space Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.4rem, 7cqi, 5rem)',     ratio: 3.33, weight: 800, lineHeight: 0.94, letterSpacing: '-0.01em' },
      heading:    { size: 'clamp(1.7rem, 3.6cqi, 2.8rem)', ratio: 2.0,  weight: 800, lineHeight: 1.0,  letterSpacing: '0' },
      subheading: { size: 'clamp(1.15rem, 2cqi, 1.5rem)',  ratio: 1.33, weight: 800, lineHeight: 1.2,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.18rem)',  ratio: 1.0,  weight: 500, lineHeight: 1.45, letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)',  ratio: 0.67, weight: 800, lineHeight: 1.4,  letterSpacing: '0.04em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'obsidian': {
    id: 'obsidian',
    name: 'Obsidian Dossier',
    mode: 'dark',
    colors: {
      background: '#0b0b0d',
      surface: '#15151a',
      text: '#e8e2d6',
      textMuted: '#8a8578',
      primary: '#d4682a',
      accent: '#b8924a',
      feedback: darkFeedback,
    },
    fonts: {
      heading: '"Cormorant Garamond", Georgia, serif',
      body: '"Spectral", Georgia, serif',
      code: '"Courier Prime", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5.4cqi, 4.1rem)', ratio: 3.33, weight: 600, lineHeight: 1.05, letterSpacing: '0.01em' },
      heading:    { size: 'clamp(1.6rem, 3cqi, 2.6rem)',   ratio: 2.0,  weight: 600, lineHeight: 1.1,  letterSpacing: '0.01em' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',   ratio: 1.33, weight: 500, lineHeight: 1.35, letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.2rem)',   ratio: 1.0,  weight: 400, lineHeight: 1.7,  letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)',  ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.06em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'lamplight': {
    id: 'lamplight',
    name: 'Lamplight',
    mode: 'dark',
    colors: {
      background: '#1a140d',
      surface: '#241d14',
      text: '#e9dfcc',
      textMuted: '#b6a888',
      primary: '#f0b054',
      accent: '#c99b52',
      feedback: darkFeedback,
    },
    fonts: {
      heading: '"Fraunces", Georgia, serif',
      body: '"Inter Tight", system-ui, sans-serif',
      code: '"IBM Plex Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(1.9rem, 4.6cqi, 3.2rem)', ratio: 3.0,  weight: 600, lineHeight: 1.08, letterSpacing: '-0.005em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.4rem)',   ratio: 2.0,  weight: 600, lineHeight: 1.15, letterSpacing: '0' },
      subheading: { size: 'clamp(1.05rem, 2cqi, 1.4rem)',  ratio: 1.33, weight: 500, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.15rem)',  ratio: 1.0,  weight: 400, lineHeight: 1.7,  letterSpacing: '0' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)',  ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.06em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
  'calm-cinematic': {
    id: 'calm-cinematic',
    name: 'Dark & Cinematic',
    mode: 'dark',
    colors: {
      background: '#0f0f0f',
      surface: '#1a1a1a',
      text: '#e8e4df',
      textMuted: '#9a9590',
      primary: '#d4a574',
      accent: '#d4a574',
      feedback: darkFeedback,
    },
    fonts: {
      heading: '"Cormorant Garamond", Georgia, serif',
      body: '"IBM Plex Sans", system-ui, sans-serif',
      code: '"JetBrains Mono", monospace',
    },
    typography: {
      baseFont: '3.7cqw',
      title:      { size: 'clamp(2.2rem, 5cqi, 4rem)',   ratio: 3.33, weight: 500, lineHeight: 1.05, letterSpacing: '-0.01em' },
      heading:    { size: 'clamp(1.5rem, 3cqi, 2.5rem)',  ratio: 2.0,  weight: 500, lineHeight: 1.2,  letterSpacing: '0' },
      subheading: { size: 'clamp(1.1rem, 2cqi, 1.5rem)',  ratio: 1.33, weight: 400, lineHeight: 1.4,  letterSpacing: '0' },
      body:       { size: 'clamp(1rem, 1.5cqi, 1.2rem)',  ratio: 1.0,  weight: 400, lineHeight: 1.75, letterSpacing: '0.005em' },
      caption:    { size: 'clamp(0.8rem, 1cqi, 0.95rem)', ratio: 0.67, weight: 400, lineHeight: 1.5,  letterSpacing: '0.04em' },
    },
    spacing: {
      padded:  'clamp(1.5rem, 4cqi, 4rem)',
      compact: 'clamp(1.25rem, 3cqi, 3rem)',
      insetX:  'clamp(1.5rem, 3.5cqi, 3.5rem)',
      inner:   'clamp(1.5rem, 2cqi, 2rem)',
      slide:   'clamp(1.5rem, 4cqi, 4rem)',
    },
  },
};

// Typography helper — returns a style object using CSS custom property font sizes
// Use this instead of manually referencing theme.typography.X.size
export function slideTypographyStyle(
  theme: Theme,
  level: TypographyLevel,
  fontOverride?: string,
): React.CSSProperties {
  const typo = theme.typography[level];
  const isHeadingLevel = level === 'title' || level === 'heading' || level === 'subheading';
  return {
    fontFamily: fontOverride || (isHeadingLevel ? theme.fonts.heading : theme.fonts.body),
    fontSize: `var(--slide-font-${level})`,
    fontWeight: typo.weight,
    lineHeight: typo.lineHeight,
    letterSpacing: typo.letterSpacing,
  };
}

// Base slide data
export interface BaseSlideData {
  template: SlideTemplate;
  /**
   * Snapshotted onto each learning_interactions row at write time. Set per-slide
   * by the author. Only meaningful for slides with an `interaction` block in the
   * registry. See INTERACTION_FRAMEWORK.md §2.
   */
  cognitiveLevel?: 'L1_recall' | 'L2_interpret' | 'L3_apply';
}

// Unified text slide with layout variants
export interface TextSlideData extends BaseSlideData {
  template: 'text';
  layout: TextSlideLayout;
  heading?: string;         // Rich HTML — title layout: big headline; others: section heading
  body?: string;            // Rich HTML — title layout: subtitle; others: main body
  secondaryBody?: string;   // Rich HTML — two-column layout: right column
  alignment?: 'left' | 'center' | 'right';
  backgroundImage?: string; // title layout only
  darkOverlay?: boolean;    // title layout only — darkens background image for text contrast
  imageUrl?: string;        // image-text layout
  imagePosition?: 'left' | 'right';
  imageSize?: 'large' | 'landscape';
  focalPoint?: { x: number; y: number };
  ratio?: '50-50' | '60-40' | '40-60'; // two-column layout
  // Unsplash attribution — applies to imageUrl (image-text) or backgroundImage (title) when Unsplash-sourced
  unsplashId?: string;
  unsplashAuthor?: string;
  unsplashAuthorUrl?: string;
}

// --- Deprecated text slide types (kept for backward compat, migrated at load time) ---

/** @deprecated Use TextSlideData with layout: 'title' */
export interface TitleSlideData extends BaseSlideData {
  template: 'title';
  headline: string;
  subtitle?: string;
  backgroundImage?: string;
  alignment?: 'left' | 'center' | 'right';
}

/** @deprecated Use TextSlideData with layout: 'body' */
export interface ContentSlideData extends BaseSlideData {
  template: 'content';
  heading?: string;
  body: string;
  media?: {
    type: 'image' | 'video';
    url: string;
    position: 'top' | 'bottom' | 'right';
  };
}

/** @deprecated Use TextSlideData with layout: 'two-column' */
export interface SplitSlideData extends BaseSlideData {
  template: 'split';
  leftContent: string;
  rightContent: string;
  ratio?: '50-50' | '60-40' | '40-60';
}

/** @deprecated Use TextSlideData with layout: 'bullets' */
export interface BulletsSlideData extends BaseSlideData {
  template: 'bullets';
  heading?: string;
  bullets: Array<{
    id: string;
    text: string;
    icon?: string;
  }>;
}

/** @deprecated Use TextSlideData with layout: 'image-text' */
export interface ImageTextSlideData extends BaseSlideData {
  template: 'image-text';
  imageUrl: string;
  imagePosition: 'left' | 'right';
  imageSize: 'large' | 'landscape';
  heading?: string;
  body: string;
}

/** @deprecated Use TextSlideData with layout: 'body' + alignment */
export interface TextBlockSlideData extends BaseSlideData {
  template: 'text-block';
  heading?: string;
  body: string;
  alignment: 'left' | 'center' | 'right';
}

// --- Non-text slide types ---

export interface ImageSlideData extends BaseSlideData {
  template: 'image';
  url: string;
  caption?: string;
  overlay?: boolean;
  focalPoint?: { x: number; y: number };
  unsplashId?: string;
  unsplashAuthor?: string;
  unsplashAuthorUrl?: string;
}

export interface VideoSlideData extends BaseSlideData {
  template: 'video';
  url: string;
  caption?: string;
}

/** @deprecated Use ImageSlideData or VideoSlideData — migrated at load time */
export interface MediaSlideData extends BaseSlideData {
  template: 'media';
  mediaType: 'image' | 'video';
  url: string;
  caption?: string;
  overlay?: boolean;
}

export interface QuizSlideData extends BaseSlideData {
  template: 'quiz';
  question: string;
  options: Array<{
    id: string;
    text: string;
    isCorrect: boolean;
    feedback?: string;
  }>;
  explanation?: string;
}

export interface WebappSlideData extends BaseSlideData {
  template: 'webapp';
  url: string;
  title?: string;
}

export interface AttuneSlideData extends BaseSlideData {
  template: 'attune';
  /** Explicit mode; unset = derive from position (send-off on the last slide,
   *  facilitator everywhere else). 'experience' is the send-off's internal id,
   *  kept for stored-record continuity. */
  mode?: 'facilitator' | 'experience';
  topic?: string;
  audience?: 'adult' | 'high-school';
}

export interface CodeSlideData extends BaseSlideData {
  template: 'code';
  heading?: string;
  code: string;
  language: string;
  showLineNumbers?: boolean;
  highlightLines?: number[];
}

export interface CanvaSlideData extends BaseSlideData {
  template: 'canva';
  embedUrl: string;
  title?: string;
}

// Reflection slide (open-ended prompts)
export interface ReflectionQuestion {
  id: string;
  prompt: string;
  placeholder?: string; // Hint text shown in the answer box
}

export interface ReflectionSlideData extends BaseSlideData {
  template: 'reflection';
  heading?: string;
  description?: string; // Brief context shown above the questions
  questions: ReflectionQuestion[];
}

export interface CoverSlideData extends BaseSlideData {
  template: 'cover';
  title: string;
  subtitle?: string;
  logoUrl?: string;
  layout: 'centered' | 'left-aligned' | 'stacked';
  showAccentBar: boolean;
  darkOverlay: boolean;    // Darken background for contrast with logo/text
  backgroundImage?: string;
  focalPoint?: { x: number; y: number };
  // Unsplash attribution for backgroundImage when Unsplash-sourced
  unsplashId?: string;
  unsplashAuthor?: string;
  unsplashAuthorUrl?: string;
}

// --- Activity slide types ---

interface ActivitySlideBase extends BaseSlideData {
  template: 'activity';
  activityType: ActivityType;
  mode: ActivityMode;
  question: string;
}

export interface LikertSlideData extends ActivitySlideBase {
  activityType: 'likert';
  mode: 'survey';
  scaleLabels: { low: string; high: string };
  scaleSize: 5 | 7;
}

export interface SurveyChoiceSlideData extends ActivitySlideBase {
  activityType: 'survey-choice';
  mode: 'survey';
  options: Array<{ id: string; text: string }>;
  allowMultiple: boolean;
}

export interface FreeTextSlideData extends ActivitySlideBase {
  activityType: 'free-text';
  mode: 'survey';
  placeholder?: string;
  maxLength?: number;
}

export interface DropdownSlideData extends ActivitySlideBase {
  activityType: 'dropdown';
  options: Array<{ id: string; text: string; isCorrect: boolean; feedback?: string }>;
}

export interface RankingSlideData extends ActivitySlideBase {
  activityType: 'ranking';
  items: Array<{ id: string; text: string }>;  // Array order = correct order
}

export interface MatchingSlideData extends ActivitySlideBase {
  activityType: 'matching';
  mode: 'quiz';
  pairs: Array<{ id: string; left: string; right: string }>;  // Pair association = correct answer
}

export type ActivitySlideData =
  | LikertSlideData
  | SurveyChoiceSlideData
  | FreeTextSlideData
  | DropdownSlideData
  | RankingSlideData
  | MatchingSlideData;

// ---------------------------------------------------------------------------
// Scenario builder templates (kula-create port — KULA_BUILDER_INTEGRATION.md §A)
// ---------------------------------------------------------------------------
// Ported verbatim from kula-create/lib/templates/templateDataContracts.ts on
// the `feature/charter-and-vibes` branch. Two changes from the source:
//   1. `TemplateBase` is replaced with `ScenarioTemplateBase extends BaseSlideData`
//      so these types satisfy SlideData. The `template` discriminator is added.
//   2. `priorContext` is dropped (per Open Question §9 — no template reads it;
//      the lesson viewer injects scenario-group context from the bridge slide).
// ---------------------------------------------------------------------------

/**
 * Match condition for an outcome-specific conclusion variant (Tier 3). Either a
 * comparison against a captured token (e.g. delta < 0) or a discrete outcome id
 * (e.g. a scenario ending branch). First matching variant wins; else the
 * top-level `body` is used. See ExperienceClose + KULA-CREATE ending plan.
 */
export type ConclusionCondition =
  | { token: string; op: 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq'; value: number | string }
  | { outcome: string };

export interface ConclusionVariant {
  /** Omit `when` for an unconditional fallback variant. */
  when?: ConclusionCondition;
  heading?: string;
  body: string;
}

/**
 * One reveal step in a staged close (§3.5.2 — no wall-of-text endings). Each
 * beat presents ONE idea, ≤ ~60 words, advanced by learner tap (never a timer);
 * revealed beats accumulate on the page. Recommended order: learner's own result
 * first, then expert model, then counter-perspective, then reflect/commit.
 */
export interface ExperienceBeat {
  heading?: string;
  body: string;
}

/**
 * The authored "So what?" close shared by every interaction type (Tier 2+3).
 * - `body`/`heading` are authored at Create time and may contain `{token}` slots
 *   filled from the learner's actual captured values at play time (Tier 2). A
 *   slot with no value is stripped gracefully — never rendered as `{token}`.
 * - `variants` are outcome-specific authored bodies (Tier 3).
 * - `reflectionPrompt` is optional articulation, included only where the author
 *   judges it adds value.
 */
export interface ExperienceConclusion {
  heading?: string;
  /** The single close paragraph. Optional when `beats` drive a staged close. */
  body?: string;
  variants?: ConclusionVariant[];
  reflectionPrompt?: string;
  /**
   * Optional staged reveal (§3.5.2). When present the close renders these beats
   * one at a time, learner-paced, instead of the single `body` block — so an
   * ending never lands as a wall of text. Tokens are filled per beat. Lives in
   * the shared close, so every interaction type + composed experience inherits it.
   */
  beats?: ExperienceBeat[];
}

interface ScenarioTemplateBase extends BaseSlideData {
  title: string;
  subtitle: string;
  /**
   * Authored "So what?" close. Optional during rollout — types adopt the shared
   * <ExperienceClose> renderer incrementally. When absent, no close is shown
   * (back-compat). See the ending plan: shared field, one renderer, no dead-ends.
   */
  conclusion?: ExperienceConclusion;
}

/**
 * A concrete artifact the learner is asked to examine — an AI draft, an email,
 * a report excerpt — authored IN FULL and shown on screen. Exists because a
 * single prose "context"/"situation" field makes scene-setting and the
 * artifact compete for one slot, and narration wins: builds shipped questions
 * about "the draft in front of you" with no draft anywhere ("show, don't
 * mention"). Structure is the strongest prompt — templates whose artifact has
 * a dedicated field (teach-back, spot-the-error, claim-evidence) never lost
 * it; this gives the interrogating templates the same guarantee.
 */
export interface ScenarioExhibit {
  /** What the artifact is — drives the frame label, not the styling. */
  kind: 'document' | 'draft' | 'email' | 'message' | 'report' | 'transcript' | 'data' | 'other';
  /** Short in-world title, e.g. "AI draft — tomorrow's discussion questions". */
  title: string;
  /** The artifact itself, complete — every line a question refers to. */
  body: string;
  /** Optional in-world source line, e.g. "Generated 14:02, prep period". */
  attribution?: string;
}

export interface RevealPredictSlideData extends ScenarioTemplateBase {
  template: 'reveal-predict';
  predictions: Array<{
    id: string;
    question: string;
    context?: string;
    options: Array<{ value: string; text: string }>;
    correctValue: string;
    revealAnswer: string;
    matchFeedback: string;
    missFeedback: string;
  }>;
  summary: { prompt: string };
}

export interface ScenarioSimulationSlideData extends ScenarioTemplateBase {
  template: 'scenario-simulation';
  /** The artifact the scenario's decisions revolve around, shown at the entry
   *  branch — separate from each branch's `situation` narration. */
  exhibit?: ScenarioExhibit;
  branches: Array<{
    id: string;
    type: 'decision' | 'ending';
    label?: string;
    situation?: string;
    question?: string;
    consequence?: string;
    choices?: Array<{
      letter: string;
      text: string;
      nextBranch: string;
      pathLabel: string;
    }>;
    title?: string;
    description?: string;
    reflection?: string;
  }>;
}

export interface SpotTheErrorSlideData extends ScenarioTemplateBase {
  template: 'spot-the-error';
  sections: Array<{
    id: string;
    instruction: string;
    context: string;
    content: Array<{
      type: 'text' | 'error' | 'decoy';
      text: string;
      errorId?: string;
      explanation?: string;
    }>;
    completionMessage: string;
  }>;
}

export interface DragSortSlideData extends ScenarioTemplateBase {
  template: 'drag-sort';
  instruction: string;
  mode: 'categorise' | 'sequence';
  items: Array<{
    id: string;
    text: string;
    correctZone: string;
    correctPosition?: number;
  }>;
  zones: Array<{ id: string; label: string }>;
  feedback: {
    allCorrect: string;
    partial: string;
    perItem?: Record<string, string>;
  };
}

export interface GalleryWalkSlideData extends ScenarioTemplateBase {
  template: 'gallery-walk';
  intro: string;
  stations: Array<{
    id: string;
    title: string;
    teaser: string;
    content: Array<{
      type: 'text' | 'image' | 'quote';
      value: string;
      attribution?: string;
    }>;
    reflectionPrompt: string;
  }>;
  synthesis: { prompt: string };
}

export interface TimelineNavigatorSlideData extends ScenarioTemplateBase {
  template: 'timeline-navigator';
  events: Array<{
    id: string;
    date: string;
    title: string;
    summary: string;
    detail: string;
    isMilestone: boolean;
    question?: string;
  }>;
  reflection: { prompt: string };
}

export interface ConfidenceMeterSlideData extends ScenarioTemplateBase {
  template: 'confidence-meter';
  items: Array<{
    id: string;
    statement: string;
    content: string;
    scale: { min: number; max: number; labels: [string, string, string] };
  }>;
  summary: { prompt: string };
}

export interface ProgressiveBuilderSlideData extends ScenarioTemplateBase {
  template: 'progressive-builder';
  baseState: string;
  layers: Array<{
    id: string;
    label: string;
    addition: string;
    transformedOutput: string;
  }>;
  reflection: { prompt: string };
}

export interface GuidedSandboxSlideData extends ScenarioTemplateBase {
  template: 'guided-sandbox';
  task: string;
  placeholder: string;
  minScore: number;
  dimensions: Array<{
    id: string;
    label: string;
    description: string;
    keywords: string[];
    tips: string;
    weight: number;
  }>;
  guidance: Record<string, string>;
}

export interface ScaffoldedInvestigationSlideData extends ScenarioTemplateBase {
  template: 'scaffolded-investigation';
  rounds: Array<{
    id: string;
    roundNumber: 1 | 2 | 3;
    mode: 'guided' | 'semi-guided' | 'independent';
    title: string;
    content: string;
    readingPauseSeconds: number;
    presetProbes: Array<{
      tag: string;
      question: string;
      response: string;
    }>;
    customProbesRequired: number;
  }>;
  debrief: { prompt: string };
}

export interface PerspectiveLensesSlideData extends ScenarioTemplateBase {
  template: 'perspective-lenses';
  stimulus: {
    context: string;
    imageAlt?: string;
  };
  /** The artifact every lens re-reads — separate from the scene-setting
   *  `stimulus.context` so neither eats the other. */
  exhibit?: ScenarioExhibit;
  lenses: Array<{
    id: string;
    label: string;
    iconKey: string;
    colour: string;
    prompt: string;
    subPrompts: Array<{
      id: string;
      text: string;
      inputType: 'text' | 'select';
      options?: string[];
      maxLength?: number;
    }>;
  }>;
  synthesis: {
    prompt: string;
    showLensComparison: boolean;
  };
}

export interface TensionMapperSlideData extends ScenarioTemplateBase {
  template: 'tension-mapper';
  tension: {
    statement: string;
    poleA: { label: string; description: string };
    poleB: { label: string; description: string };
  };
  factors: Array<{
    id: string;
    label: string;
    description: string;
  }>;
  expertAnalysis: {
    summary: string;
    insights: Array<{
      factorId: string;
      perspective: string;
    }>;
  };
  reflection: { prompt: string };
}

export interface BeforeAfterBridgeSlideData extends ScenarioTemplateBase {
  template: 'before-after-bridge';
  beforePhase: {
    instruction: string;
    prompts: Array<{
      id: string;
      beforeText: string;
      inputType: 'text' | 'scale';
      scaleLabels?: [string, string];
      maxLength?: number;
    }>;
  };
  contentPhase: {
    instruction: string;
    blocks: Array<{
      id: string;
      type: 'text' | 'data' | 'quote';
      content: string;
      source?: string;
    }>;
  };
  afterPhase: {
    instruction: string;
    prompts: Array<{
      id: string;
      afterText: string;
    }>;
  };
  comparison: { reflectionPrompt: string };
}

export interface ClaimEvidenceSlideData extends ScenarioTemplateBase {
  template: 'claim-evidence';
  claim: {
    statement: string;
    context?: string;
  };
  evidencePool: Array<{
    id: string;
    label: string;
    detail: string;
    strength: 'strong' | 'partial' | 'distractor';
    expertReasoning?: string;
  }>;
  argumentRules: {
    minEvidence: number;
    maxEvidence: number;
    requireReasoning: boolean;
  };
  counterClaim?: {
    statement: string;
    strongEvidence: string[];
  };
  expertModel: {
    selectedEvidence: string[];
    reasoning: string;
  };
  reflection: { prompt: string };
}

export interface LayeredRevealSlideData extends ScenarioTemplateBase {
  template: 'layered-reveal';
  scenario: { context: string };
  layers: Array<{
    id: string;
    label: string;
    content: string;
    contentType: 'text' | 'data' | 'quote' | 'image-description';
    assessmentPrompts: Array<{
      id: string;
      text: string;
      inputType: 'text' | 'select' | 'scale';
      options?: string[];
      scaleLabels?: [string, string];
      maxLength?: number;
    }>;
  }>;
  synthesis: {
    revealText: string;
    reflectionPrompt: string;
    showAssessmentTrail: boolean;
  };
}

interface ReflectActStage {
  id: string;
  label: string;
  description: string;
  prompts: Array<{
    id: string;
    text: string;
    inputType: 'text' | 'select';
    options?: string[];
    maxLength?: number;
    placeholder?: string;
  }>;
}

// 17th interaction type — restored from Dylan's original Kula Create catalogue.
// In the source the engine existed in interaction-presets.md but neither the
// Analyst nor the Designer was ever taught to select it (see
// Kula-Create-Pedagogical-Foundations.md §A2). Built here for the LMS port.
// Grounding: dual coding (Paivio), spatial cognition, self-directed exploration.
export interface HotspotExplorerSlideData extends ScenarioTemplateBase {
  template: 'hotspot-explorer';
  /** Hosted image URL — works with the same image-upload flow as ImageSlide. */
  imageUrl: string;
  /** Required for a11y. Describes what the diagram/image shows so a screen-
   *  reader user has the same orienting context a sighted user gets. */
  imageAlt: string;
  /** Optional caption rendered under the image. */
  imageCaption?: string;
  hotspots: Array<{
    id: string;
    /** Position as 0–100 percentage of image width — keeps placements
     *  responsive across viewports. */
    x: number;
    /** Position as 0–100 percentage of image height. */
    y: number;
    /** Short label shown next to the numbered marker (e.g. "Cooling tower"). */
    label: string;
    /** Detail panel heading. */
    title: string;
    /** Detail panel body — 1–3 short paragraphs. */
    body: string;
    /** Optional secondary content type marker for styling cues. */
    contentType?: 'text' | 'data' | 'quote';
  }>; // 3-8 hotspots — fewer feels sparse, more overwhelming
  /** Synthesis block that unlocks once every hotspot has been explored. */
  synthesis: {
    title: string;
    /** 2–3 sentences tying the hotspots together. */
    body: string;
    /** A reflection prompt the learner answers after the synthesis lands. */
    reflectionPrompt: string;
  };
}

export interface ReflectActSlideData extends ScenarioTemplateBase {
  template: 'reflect-act';
  stimulus?: {
    text: string;
    source?: string;
  };
  /** Fixed 3-stage tuple: what / so-what / now-what */
  stages: [
    ReflectActStage & { id: 'what' },
    ReflectActStage & { id: 'so-what' },
    ReflectActStage & { id: 'now-what' },
  ];
  summary: {
    showFullReflection: boolean;
    exportEnabled: boolean;
    closingMessage?: string;
  };
}

// Source-examination — exam-style revision built from one source document plus a
// scaffolded ladder of L1→L2→L3 questions, each with built-in skills coaching.
// One slide = one source; a revision module is several of these composed by the
// Create pipeline. See REVISION_TEMPLATE_PLAN.md. The 7 skill types drive which
// coaching drawer a question shows (content lives in sourceExamCoaching.ts).
export type SourceExamSkill =
  | 'extraction'
  | 'concept'
  | 'interpretation'
  | 'analysis'
  | 'reliability'
  | 'comparison'
  | 'synthesis';

export interface SourceExaminationQuestion {
  /** Stable activity_key for this sub-question — one learning_interactions row each. */
  id: string;
  /** Display number, e.g. "1.1.1". */
  number: string;
  /** Which of the 7 skill types — selects the coaching drawer. */
  skill: SourceExamSkill;
  /** Snapshotted onto the interaction row; drives the per-level results bars. */
  cognitiveLevel: 'L1_recall' | 'L2_interpret' | 'L3_apply';
  /** Exam marks this question is worth. */
  marks: number;
  /** Recommended time in seconds (drives the green/amber/red timer). */
  recommendedTimeSec?: number;
  prompt: string;
  /** Optional in-question scaffolding hint, shown above the options. */
  hint?: string;
  options: string[];
  /** Correct option index, or array of indices for multi-select. */
  correct: number | number[];
  /** Present (and ≥2) makes this a multi-select question; caps the selection. */
  multiSelectMax?: number;
  /** Shown after submit — explains WHY, the load-bearing feedback. */
  feedback: string;
}

export interface SourceExaminationSlideData extends ScenarioTemplateBase {
  template: 'source-examination';
  /**
   * Shared id for all slides generated as one revision module. Lets the slides
   * (rendered independently by the viewer) roll up a module-level total and gate
   * the Mark This Answer bonus. Stamped at generation time. See moduleProgress.ts.
   */
  moduleId?: string;
  /**
   * Exam-mode timer. Default on (reference behaviour, framed as a guide not a
   * penalty); set false to hide all timers. Author/learner toggle.
   */
  timed?: boolean;
  source: {
    /** e.g. "Source 1A". */
    label: string;
    /** e.g. "Speech Extract", "Political Cartoon". */
    type: string;
    /** Rich text / HTML body. Optional when the source is purely visual. */
    body?: string;
    /** Hosted image URL for visual sources (cartoons, photos, maps). */
    imageUrl?: string;
    /** Alt text — required for a11y when imageUrl is set. */
    imageAlt?: string;
    attribution: string;
  };
  questions: SourceExaminationQuestion[];
}

// Mark This Answer — the evaluative companion to source-examination. The learner
// grades sample answers against a rubric (the inverse skill: think like an
// examiner). Composes as the closer of a revision module. See
// REVISION_TEMPLATE_PLAN.md §3.
export interface MarkThisAnswerCriterion {
  id: string;
  prompt: string;
  /** The examiner-correct judgement for this criterion. */
  correct: 'yes' | 'partial' | 'no';
  /** Shown after the learner commits — explains the judgement. */
  explanation: string;
}

export interface MarkThisAnswerSampleAnswer {
  /** Stable activity_key — one learning_interactions row per answer evaluated. */
  id: string;
  label: string;
  /** The sample answer the learner evaluates. */
  text: string;
  /**
   * Mark range this answer would score, revealed ONLY after the learner commits
   * their judgement (judge-blind rule, REVISION_TEMPLATE_PLAN.md §11 #6). A mark
   * range with NO level label, e.g. "4–5 marks" — never "4–5 (Level 2)".
   */
  approxBand?: string;
  criteria: MarkThisAnswerCriterion[];
}

/**
 * The learner writes a full CAPS History essay and the marking engine reads it
 * — four sequential model calls producing a coached report, no mark and no
 * level (see KULA_INTEGRATION_SPEC_for_luke.md Piece D).
 *
 * A native slide rather than a `webapp` embed, and that is the whole point: an
 * iframe cannot be gated on the learner's marking allowance, cannot be metered,
 * and cannot write to learning_interactions. Marking costs real money per
 * submission, so it has to run behind POST /api/ai/mark-essay.
 *
 * The AUTHOR fixes which past-paper question this slide marks against. Lessons
 * here are already topic-shaped ("Cold War: Vietnam", "Black Consciousness"), so
 * asking the learner to pick would be a step that answers itself.
 */
export interface EssayMarkingSlideData extends ScenarioTemplateBase {
  template: 'essay-marking';
  /**
   * Which examining board this lab runs as — 'nsc' (CAPS) or 'ieb'.
   *
   * ONE lab, two products: the board picks the question set, the memo and the
   * marking grid, and it is carried through the handshake to the endpoint,
   * which resolves the pack for {board, topicId, year, sitting, paper}. The
   * question coordinates below are only unique WITHIN a board.
   *
   * Optional because every slide authored before boards existed is NSC, and
   * `asBoard` in lib/marking/board.ts resolves absent to exactly that. Read it
   * through that helper, never as a bare `data.board`.
   */
  board?: Board;
  /** Topic id from the engine's registry, e.g. 'vietnam_extension'. */
  topicId: string;
  year: number;
  /** 'Nov' for every question currently packed. */
  sitting: string;
  /** 'P1' | 'P2'. */
  paper: string;
  /** Author-facing reminder of the question; display copy comes from the engine. */
  questionLabel?: string;
}

export interface MarkThisAnswerSlideData extends ScenarioTemplateBase {
  template: 'mark-this-answer';
  /**
   * Shared revision-module id (see SourceExaminationSlideData.moduleId). When
   * set, the bonus is gated until the learner has scored ≥70% across the
   * module's source slides. Unset = always open (stand-alone use).
   */
  moduleId?: string;
  /** What the sample answers were responding to (the original question). */
  task: string;
  /** Optional rubric framing, e.g. the level descriptors. */
  rubricNote?: string;
  answers: MarkThisAnswerSampleAnswer[];
}

// Revision Exam — the SINGLE self-contained revision interactive (the faithful
// Kula port of the reference exam app). ONE slide holds the whole paper: every
// source, a continuous question flow, global + per-question timers, the skills
// coach, a unified Level-1–7 results screen, and the 70%-unlock Mark This Answer
// bonus. Faithful to the reference's SRC + QS (flat) + MTA structure.
// See REVISION_TEMPLATE_PLAN.md.

// One step of the guided 8-mark paragraph builder (synthesis). Pick-from-choices
// only — never AI-graded free text. The four steps walk topic sentence → source
// evidence → own knowledge → conclusion. See REVISION_TEMPLATE_PLAN.md §11 #7.
export interface RevisionParagraphStep {
  /** Stable activity_key — one learning_interactions row per step. */
  id: string;
  /** Short label, e.g. "Topic sentence". */
  label: string;
  prompt: string;
  options: string[];
  /** Correct option index (single-select). */
  correct: number;
  /** Exam marks for this step (the four sum to the paragraph's mark value). */
  marks: number;
  /** Shown after submit — explains the choice. */
  feedback: string;
}

export interface RevisionExamSource {
  /** Short key referenced by questions, e.g. "1A". */
  key: string;
  /** Display label, e.g. "Source 1A". */
  label: string;
  /** e.g. "Speech Extract", "Political Cartoon". */
  type: string;
  /** Rich text / HTML body. Optional for purely visual sources. */
  body?: string;
  /** Hosted image URL for visual sources. */
  imageUrl?: string;
  /** Alt text — required when imageUrl is set. */
  imageAlt?: string;
  attribution: string;
}

export interface RevisionExamQuestion {
  /** Stable activity_key — one learning_interactions row per question. */
  id: string;
  /** Which source this question interrogates (RevisionExamSource.key). */
  sourceKey: string;
  /** Display number, e.g. "1.1.1". */
  number: string;
  skill: SourceExamSkill;
  cognitiveLevel: 'L1_recall' | 'L2_interpret' | 'L3_apply';
  marks: number;
  recommendedTimeSec?: number;
  prompt: string;
  hint?: string;
  options: string[];
  correct: number | number[];
  /** Present (and ≥2) makes this a multi-select question; caps the selection. */
  multiSelectMax?: number;
  feedback: string;
}

/**
 * A downloadable source document that backs the exam — the original PDF the
 * module was generated from, hosted in the public `course-assets` bucket so
 * students can work from the real paper alongside the interactive.
 *
 * These are published exam papers: public data, so the bucket is public and the
 * URL is the only handle. Do NOT put anything confidential here. The one thing
 * that IS withheld is pedagogical, not secret: the memo defaults to
 * `after-results` so the answer key doesn't arrive before the attempt.
 */
export interface RevisionExamAttachment {
  /** Which of the three CAPS documents this is. */
  kind: 'questionPaper' | 'addendum' | 'memo';
  /** Public URL in the course-assets bucket. */
  url: string;
  /** Original filename — the download filename and the visible label. */
  fileName: string;
  /** Size in bytes, for the "2.4 MB" hint. */
  sizeBytes?: number;
  /**
   * When students may download it. `after-results` gates the file behind
   * finishing the exam (the memo's default — an answer key handed out up front
   * defeats the exercise). `hidden` keeps it for the instructor only.
   */
  visibility: 'always' | 'after-results' | 'hidden';
}

export interface RevisionExamSlideData extends ScenarioTemplateBase {
  template: 'revision-exam';
  /** Exam-mode timers (per-question + global). Default on. */
  timed?: boolean;
  /** Downloadable original papers (question paper / addendum / memo). */
  attachments?: RevisionExamAttachment[];
  /** The source documents, keyed by `key`. */
  sources: RevisionExamSource[];
  /** Continuous, ordered question list (like the reference QS array). */
  questions: RevisionExamQuestion[];
  /**
   * Optional guided 8-mark synthesis paragraph (Q1.6 style). Four pick-from-
   * choices steps. Its marks count toward the score, results and unlock gate.
   */
  paragraphBuilder?: {
    task: string;
    intro?: string;
    steps: RevisionParagraphStep[];
  };
  /** Optional bonus, unlocked once the learner scores ≥ unlockPct. */
  markThisAnswer?: {
    task: string;
    rubricNote?: string;
    answers: MarkThisAnswerSampleAnswer[];
  };
  /** Pass mark to unlock the bonus. Default 70. */
  unlockPct?: number;
}

// Auto-inserted between scenario-group slides — narrative continuity beat.
export interface BridgeSlideData extends BaseSlideData {
  template: 'bridge';
  narrativeText: string;
  showResponseRecap: boolean;
  /** Resolved client-side from lesson state — points at the slide whose response we recap. */
  priorSlideId?: string;
}

/**
 * Teach-Back — the learner coaches a struggling student. Beats:
 *  1. red-pen the artifact (strike damage phrases; over-striking the loadBearing line is punished)
 *  2. diagnose the belief underneath (select the correct misconception)
 *  3. compose coaching from ordered fragments (order + inclusion pick the branch)
 *  4. the student responds (aha / partial / backfire / dependency; optional one adapt beat)
 *  5. the residue check (strike the belief's fingerprint in the student's rewrite)
 *  6. authored close (via `conclusion`, with {caught}/{total}/{diagnosed} tokens)
 * See CREATE-COMPOSITION-HANDOVER.md §9 / §9.1.
 */
export type TeachBackCoachKind = 'perspective' | 'targeted' | 'ownership' | 'backfire' | 'answer';

export interface TeachBackArtifactFragment {
  id: string;
  text: string;
  /** false = fixed connective text (greeting, sign-off) — not tappable. */
  strikeable: boolean;
  /** true = a phrase the learner SHOULD strike. */
  damage: boolean;
  /** true = the one load-bearing line a careless reviewer wrongly strikes. */
  loadBearing?: boolean;
}

export interface TeachBackBranch {
  id: string;
  /** All present predicates must hold (AND); first matching branch wins. */
  when: {
    opensWithKind?: TeachBackCoachKind;
    includesAnyKind?: TeachBackCoachKind[];
    excludesAllKinds?: TeachBackCoachKind[];
  };
  outcome: 'aha' | 'partial' | 'backfire' | 'dependency';
  /** The student's reply in their own voice. */
  studentReply: string;
  /** Feedback to the learner about what their move did. */
  coachNote: string;
  /** backfire/dependency may offer one adapt beat (uses the shared adaptBeat). */
  allowsAdapt?: boolean;
}

export interface TeachBackSlideData extends ScenarioTemplateBase {
  template: 'teach-back';
  instruction: string;
  student: {
    name: string;
    role: string;
    /** The student's opening defence of their work. */
    voiceOpening: string;
  };
  artifact: {
    kind: 'email' | 'message' | 'document' | 'code' | 'plan';
    /** Header rows, e.g. To: / Subject:. */
    header?: Array<{ label: string; value: string }>;
    fragments: TeachBackArtifactFragment[];
  };
  /** id of the correct diagnosisOption. */
  misconceptionId: string;
  diagnosisOptions: Array<{
    id: string;
    text: string;
    correct: boolean;
    /** Why this is right, or why this reading misleads. */
    feedback: string;
  }>;
  coachingFragments: Array<{ id: string; text: string; kind: TeachBackCoachKind }>;
  /** Max fragments the learner may compose into their coaching (≤3). */
  maxCoaching: number;
  branches: TeachBackBranch[];
  /** Fallback when no branch matches. */
  defaultBranch: { outcome: 'partial'; studentReply: string; coachNote: string };
  /** One adapt beat offered after a backfire/dependency outcome. */
  adaptBeat?: {
    prompt: string;
    options: Array<{
      id: string;
      text: string;
      kind: TeachBackCoachKind;
      leadsTo: 'aha' | 'unresolved';
      studentReply: string;
      coachNote: string;
    }>;
  };
  /** Beat 5 — the student's rewrite, improved but carrying one residue fragment. */
  revision: {
    kind: 'email' | 'message' | 'document' | 'code' | 'plan';
    header?: Array<{ label: string; value: string }>;
    fragments: Array<{ id: string; text: string; strikeable: boolean; isResidue: boolean }>;
  };
  /** The expert teacher's contrasting move, shown at the close. */
  expertNote: string;
}

/**
 * Composed experience (Kula Create Workshop, Phase 1). A bespoke interaction the
 * AI/designer assembles from capturing primitive blocks (see
 * lib/scenario-builder/composition/types.ts). Renders as a native Kula component
 * and reports one composite capture record. The `conclusion` close is inherited
 * from ScenarioTemplateBase and supports block-scoped {id.token} slots + beats.
 */
export interface ComposedExperienceSlideData extends ScenarioTemplateBase {
  template: 'composed-experience';
  /** One-line designer intent — the point of the experience. */
  intent: string;
  blocks: CompositionBlock[];
  scoring: CompositionScoring;
}

export type ScenarioSlideData =
  | RevealPredictSlideData
  | ScenarioSimulationSlideData
  | SpotTheErrorSlideData
  | DragSortSlideData
  | GalleryWalkSlideData
  | TimelineNavigatorSlideData
  | ConfidenceMeterSlideData
  | ProgressiveBuilderSlideData
  | GuidedSandboxSlideData
  | ScaffoldedInvestigationSlideData
  | PerspectiveLensesSlideData
  | TensionMapperSlideData
  | BeforeAfterBridgeSlideData
  | ClaimEvidenceSlideData
  | LayeredRevealSlideData
  | HotspotExplorerSlideData
  | ReflectActSlideData
  | TeachBackSlideData
  | ComposedExperienceSlideData
  | SourceExaminationSlideData
  | MarkThisAnswerSlideData
  | EssayMarkingSlideData
  | RevisionExamSlideData;

/**
 * Interactive slot — a placeholder unit in the outline that says "build me in
 * Create" before the actual interactive content exists. Inserted by `+
 * Interactive` in the Workbench toolbar or by a Designer outline import.
 * Phase E (Create) replaces the slot slide with the built scenario group's
 * slides via `applyScenarioGroupForSlot`.
 */
export interface InteractiveSlotSlideData extends BaseSlideData {
  template: 'interactive-slot';
  /** Plain-language description of what Create should build. Edited inline. */
  brief?: string;
  /** Optional Designer-supplied verb ("predict", "decide", "explore"...) */
  anchorVerb?: string;
  /**
   * Pedagogical modality this slot realises — Scenario / Challenge / Apply /
   * Reflection / Explore. Stamped by the conversational Designer (defaults to
   * the lesson modality); read by the Create walkthrough as the default for
   * the Step 1 modality picker. See lib/scenario-builder/modality.ts.
   */
  modality?: 'scenario' | 'challenge' | 'apply' | 'reflection' | 'explore';
  /**
   * Position role in a multi-slot lesson: opener / core / closer. Stamped by
   * the Designer when it places the slot. Surfaced as a tag in the Workbench
   * unit strip. Soft constraint, not enforced.
   */
  position?: 'opener' | 'core' | 'closer';
  /**
   * Snapshot of the lesson's authoring context, stamped onto the slot at
   * outline-import time by the conversational Designer (see SketchSurface's
   * handleImportJson). The Create walkthrough pre-fills Step 1 + Step 2 from
   * this so the author isn't asked to re-state what the Designer already
   * captured. All fields optional; the slot still works without any of them.
   * Snapshot, not live binding — editing course/lesson context later won't
   * retroactively update existing slots.
   */
  lessonContext?: {
    audience?: string;
    grade?: string;
    programme?: string;
    tone?: string;
    /** Plain-language outcome the lesson is aiming for (used as Create Step 2's `outcome` seed). */
    lessonOutcome?: string;
    /** One-sentence summary of the lesson's narrative arc (helps Create's persona + entry strategy). */
    narrativeArc?: string;
  };
  /**
   * Set when Phase D imports a Designer outline that pre-binds the slot to a
   * planned scenario group id. Phase E uses it to associate the eventual
   * group's metadata with this slot's position.
   */
  plannedScenarioGroupId?: string;
}

// ---------------------------------------------------------------------------
// Activity Doors — generated interactives (KULA-ACTIVITY-DOORS-TOP10.md).
// A door is a named activity type a designer recognises (Scenario, Case Study,
// Mark the Work…). Under the ratified fork B, every door generates a sealed
// self-contained HTML interactive rather than data for a fixed renderer.
// Door definitions (names, worlds, intake copy) live in lib/doors/doors.ts.
// ---------------------------------------------------------------------------

export type DoorKey = 'scenario' | 'case-study' | 'mark-the-work' | 'debate' | 'card-sort';

/**
 * The standard door intake — the short question flow every door asks before
 * generating. Ratified base: context · use the lesson slides as source? ·
 * what it should achieve · short or long · pick a world (each door carries
 * its own default world).
 */
export interface DoorIntake {
  /** What's the situation / the material the interactive works on. */
  context: string;
  /** Opt-in: populate the brief from the lesson slides before this one. */
  useLessonSource: boolean;
  /** What the learner should walk away with. */
  goal: string;
  length: 'short' | 'long';
  /** Theme world delivered as a visual-language brief in the generation prompt. */
  world: ThemeId;
}

/**
 * A generated interactive — one Activity Door build. `html` is the sealed
 * self-contained experience (absent until the first build lands; the slide
 * renders as a ready-to-build stub meanwhile). Only the CURRENT accepted
 * version is stored here: refine rounds happen in the build studio session,
 * and persisting every round would bloat the lesson JSONB the autosave loop
 * carries. Provenance (door, intake, receipt) travels with the slide so a
 * rebuild can always be re-briefed from what produced it.
 */
export interface GeneratedInteractiveSlideData extends BaseSlideData {
  template: 'generated-interactive';
  door: DoorKey;
  intake: DoorIntake;
  /** The sealed self-contained HTML build students play. */
  html?: string;
  /**
   * Fabrication receipt — the build's INVENTED: line, surfaced to the author
   * ("kula invented … check these ring true"). Authors are the only people
   * who can catch a fabricated artifact that rings false.
   */
  receipt?: string;
  buildMeta?: {
    model: string;
    builtAt: string; // ISO timestamp
    refinementCount: number;
  };
}

/**
 * A Kula Revise run: one lesson, knowledge check, Practice Lab paper or
 * skeleton builder, played end to end inside one slide. The content contract
 * (`run`) lives in lib/revise/types.ts and is shared byte-for-byte with the
 * validator and the MCP that writes it; this wrapper only adds the slide chrome.
 * See KULA-REVISE-LAB-ENGINE-PLAN.md.
 */
export interface ReviseRunSlideData extends ScenarioTemplateBase {
  template: 'revise-run';
  run: ReviseRun;
}

export type SlideData =
  | TextSlideData
  | CoverSlideData
  | ImageSlideData
  | VideoSlideData
  | QuizSlideData
  | ActivitySlideData
  | WebappSlideData
  | AttuneSlideData
  | CodeSlideData
  | CanvaSlideData
  | ReflectionSlideData
  | ScenarioSlideData
  | BridgeSlideData
  | InteractiveSlotSlideData
  | GeneratedInteractiveSlideData
  | ReviseRunSlideData
  // Deprecated — kept for backward compat (migrated at load time)
  | MediaSlideData
  | TitleSlideData
  | ContentSlideData
  | SplitSlideData
  | BulletsSlideData
  | ImageTextSlideData
  | TextBlockSlideData;

export interface Slide {
  id: string;
  data: SlideData;
  order: number;
  notes?: string; // Instructor notes
  required?: boolean; // If true, student must view this slide before advancing past it
  submittable?: boolean; // If true, student response is sent to instructor for grading
  modeOverride?: ThemeMode; // If set, overrides the course theme mode for this slide
  /**
   * Per-slide theme override — the "slide skin" (KULA-CREATE-LIBRARY-PLAN.md
   * Phase 4, behind `slide_skins`). Dress only: the slide's words, answers and
   * scoring never change. Resolved by getSlideTheme with lower precedence than
   * a scenario-group override (groups carry experiences, which own their world)
   * and tenant-brand (brand always wins). Absent → inherit the course theme.
   */
  themeOverride?: ThemeId;
  /**
   * Incremented on every save. Snapshotted onto each learning_interactions row
   * so longitudinal analytics stay honest across content edits (a "q1" before
   * and after a restructure are different things). See INTERACTION_FRAMEWORK.md §2.
   */
  revision?: number;
  /**
   * Scenario group binding. Slides belonging to the same scenarioGroupId share
   * persistent chrome (throughline question + persona) and a continuous visual
   * style in the lesson viewer. Set by the AI compositor; not user-editable.
   * The group's metadata lives on `SlideBasedContent.scenarioGroups`.
   */
  scenarioGroupId?: string;
}

/**
 * Scenario group metadata. One per multi-template AI build. Sits on
 * `SlideBasedContent` alongside the slides; binds to slides via
 * `Slide.scenarioGroupId`. See KULA_BUILDER_INTEGRATION.md §Phase 5.
 *
 * Phase 9 will promote this to a first-class `experiences` table; the JSON
 * here will become a stale projection at that point. Keep the shape stable.
 */
export interface ScenarioGroup {
  id: string;
  /**
   * Provocative question persistent across the group's chrome. LEARNER-FACING —
   * rendered verbatim by ScenarioGroupChrome. Only ever set to authored copy,
   * never to the raw build brief (that's `sourceBrief`).
   */
  throughlineQuestion?: string;
  /**
   * The instructor's raw Kula Create brief that produced this group. Build-time
   * traceability only — never rendered to learners.
   */
  sourceBrief?: string;
  /** Role framing — e.g. "You are the Chief AI Officer at...". */
  persona?: string;
  /**
   * Designer's selected creative direction (concept title + photo + recommended
   * style). `recommendedStyle` is build-time METADATA only — the AI's suggestion,
   * surfaced on concept cards. It does NOT drive rendering; that's `themeOverride`.
   */
  creativeDirection?: {
    title: string;
    description?: string;
    photoQuery?: string;
    photoUrl?: string;
    recommendedStyle?: ThemeId;
  };
  /**
   * The group's effective theme override — the single field rendering reads
   * (builder canvas, builder preview, and the student viewer all resolve it
   * through getSlideTheme, so every surface agrees). Stamped from the AI's
   * recommendedStyle at apply time; the author can change or clear it via the
   * group theme chip. Absent → slides inherit the course theme.
   */
  themeOverride?: ThemeId;
  /** Per-transition narrative text (N-1 entries for N scenario slides). */
  bridgeTexts?: string[];
  /** Whether the AI applied a narrative wrapper voice across the group. */
  narrativeWrapper?: boolean;
  /**
   * The interaction-type sequence that produced this group. Useful for
   * authoring tools (re-validate position rules, surface in UI). Matches
   * the `template` discriminators of the underlying scenario slides.
   */
  interactionSequence?: string[];
}

// SUBMITTABLE_TEMPLATES is derived from SLIDE_REGISTRY — see './slideRegistry'.

// Light/dark theme pairings. Only themes with a true paired counterpart support
// per-slide mode overrides. `warm` and `ocean` will be added once paired themes
// ship. `tenant-brand` is always paired (tenants define both variants in their
// brand editor).
//
// Phase 7 decision: the 4 kula-create themes (`warm-playful`, `editorial`,
// `bold-modern`, `calm-cinematic`) ship UNPAIRED — deliberately. Each is a
// standalone visual register with its own contrast story (editorial leans
// austere, warm-playful leans saturated, etc). Authoring dark counterparts
// would dilute the register, not extend it. Authors who need a dark scenario
// preset should pick `modern-dark`. `courseSupportsModeOverride()` therefore
// returns false for these themes and the modeOverride toggle stays hidden.
// Revisit only when a tenant signal contradicts this.
const LIGHT_DARK_PAIRS: Partial<Record<ThemeId, { light: ThemeId; dark: ThemeId }>> = {
  'modern-light':  { light: 'modern-light',  dark: 'modern-dark' },
  'modern-dark':   { light: 'modern-light',  dark: 'modern-dark' },
  'tenant-brand':  { light: 'tenant-brand',  dark: 'tenant-brand' },
};

export const courseSupportsModeOverride = (courseThemeId: ThemeId): boolean =>
  Boolean(LIGHT_DARK_PAIRS[courseThemeId]);

// Applies a BrandSlideTheme variant onto the placeholder Theme, overriding
// colours + font stack while inheriting typography/spacing/feedback.
const applyBrandVariant = (
  brandTheme: BrandSlideTheme,
  mode: ThemeMode,
  fontStack: string,
): Theme => {
  const base = themes['tenant-brand'];
  const palette = brandTheme.variants[mode] ?? brandTheme.variants.light;
  return {
    ...base,
    mode,
    name: brandTheme.name,
    colors: {
      ...base.colors,
      background: palette.background,
      surface: palette.surface,
      text: palette.text,
      textMuted: palette.textMuted,
      primary: palette.primary,
      accent: palette.accent,
      feedback: mode === 'dark' ? darkFeedback : lightFeedback,
    },
    fonts: {
      ...base.fonts,
      heading: fontStack,
      body: fontStack,
    },
  };
};

/**
 * Resolve the course-level theme, substituting the tenant brand theme when
 * `themeId === 'tenant-brand'` and a brand theme is provided. Callers without
 * access to branding (exports, offline tooling) can pass undefined and will
 * get the placeholder theme back.
 */
export const resolveTheme = (
  themeId: ThemeId,
  brandTheme?: BrandSlideTheme | null,
  brandFontStack?: string,
): Theme => {
  if (themeId === 'tenant-brand' && brandTheme) {
    return applyBrandVariant(brandTheme, 'light', brandFontStack ?? '"Inter", system-ui, sans-serif');
  }
  return themes[themeId];
};

export const getSlideTheme = (
  slide: Slide,
  courseThemeId: ThemeId,
  brandTheme?: BrandSlideTheme | null,
  brandFontStack?: string,
  /**
   * Optional scenario-group theme override — pass `group.themeOverride` (the
   * explicit field; NOT creativeDirection.recommendedStyle, which is build-time
   * metadata). When set, the group's theme supersedes the course theme so every
   * slide + bridge in the group resolves the same theme. Builder canvas,
   * builder preview and the student viewer must all pass it so the author
   * sees exactly what students get.
   */
  groupThemeOverride?: ThemeId,
): Theme => {
  // Precedence: brand > group override > slide override > course theme.
  // tenant-brand always honours brand; the group override beats the slide's
  // own skin (`slide.themeOverride`) because groups carry experiences, which
  // own their world.
  const effectiveCourseTheme: ThemeId =
    courseThemeId === 'tenant-brand'
      ? courseThemeId
      : groupThemeOverride ?? slide.themeOverride ?? courseThemeId;

  if (effectiveCourseTheme === 'tenant-brand' && brandTheme) {
    const mode: ThemeMode = slide.modeOverride ?? 'light';
    return applyBrandVariant(brandTheme, mode, brandFontStack ?? '"Inter", system-ui, sans-serif');
  }
  const pair = LIGHT_DARK_PAIRS[effectiveCourseTheme];
  if (!slide.modeOverride || !pair) return themes[effectiveCourseTheme];
  return themes[pair[slide.modeOverride]];
};

export interface SlideBasedContent {
  version: 2;
  theme: ThemeId;
  slides: Slide[];
  /**
   * Scenario group metadata (Phase 5). Each group ties together the scenario
   * slides + bridges produced by one AI compositor run. Slides bind to a group
   * via their `scenarioGroupId`. Empty / absent on lessons authored manually.
   */
  scenarioGroups?: ScenarioGroup[];
  /**
   * Pedagogical modality the lesson is realising — Scenario / Challenge /
   * Apply / Reflection / Explore. Stamped at outline-import time by the
   * conversational Designer (mirrors `designer_session.context.modality`)
   * so callers can filter/query lessons by modality without joining sessions.
   * Independent per-slot modality lives on InteractiveSlotSlideData; this
   * field is the lesson-level intent.
   */
  modality?: 'scenario' | 'challenge' | 'apply' | 'reflection' | 'explore';
  /**
   * Lesson-level narrative persona — Design Rule 1 (Narrative Identity).
   * Committed once during conversational Designer Stage 1 via set_persona,
   * stamped onto every Interactive slot in the lesson. Create's Designer-spec
   * stage reads this and seeds the entry strategy with it instead of inventing
   * a fresh persona, so a pair of slots in one lesson can't cast the learner
   * as different people. One persona per lesson (locked decision).
   */
  persona?: {
    role: string;
    context: string;
    stakes: string;
  };
}

/**
 * Resolve the scenario group a slide belongs to, if any. Returns undefined
 * for slides outside any group (manually-authored or AI single-template builds).
 */
export const getScenarioGroupForSlide = (
  slide: Slide,
  content: SlideBasedContent,
): ScenarioGroup | undefined => {
  if (!slide.scenarioGroupId) return undefined;
  return content.scenarioGroups?.find((g) => g.id === slide.scenarioGroupId);
};

// Helper to check if content is slide-based
export const isSlideBasedContent = (content: unknown): content is SlideBasedContent => {
  return (
    typeof content === 'object' &&
    content !== null &&
    'version' in content &&
    (content as any).version === 2 &&
    'slides' in content &&
    Array.isArray((content as any).slides)
  );
};

// Helper to create empty slide content
export const createEmptySlideContent = (theme: ThemeId = 'modern-light'): SlideBasedContent => ({
  version: 2,
  theme,
  slides: [],
});

// Escape HTML entities for migration
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Migrate deprecated slide types to the unified text type
export function migrateSlideData(data: SlideData): SlideData {
  switch (data.template) {
    case 'title': {
      const d = data as TitleSlideData;
      return {
        template: 'text',
        layout: 'title',
        heading: d.headline || '',
        body: d.subtitle || '',
        alignment: d.alignment || 'center',
        backgroundImage: d.backgroundImage,
      } as TextSlideData;
    }
    case 'content': {
      const d = data as ContentSlideData;
      if (d.media?.url) {
        return {
          template: 'text',
          layout: 'image-text',
          heading: d.heading || '',
          body: d.body || '',
          imageUrl: d.media.url,
          imagePosition: d.media.position === 'right' ? 'right' : 'left',
          imageSize: 'landscape',
        } as TextSlideData;
      }
      return {
        template: 'text',
        layout: 'body',
        heading: d.heading || '',
        body: d.body || '',
      } as TextSlideData;
    }
    case 'bullets': {
      const d = data as BulletsSlideData;
      const bulletHtml = d.bullets.length > 0
        ? `<ul>${d.bullets.map(b => `<li>${escapeHtml(b.text)}</li>`).join('')}</ul>`
        : '<ul><li></li></ul>';
      return {
        template: 'text',
        layout: 'bullets',
        heading: d.heading || '',
        body: bulletHtml,
      } as TextSlideData;
    }
    case 'text-block': {
      const d = data as TextBlockSlideData;
      return {
        template: 'text',
        layout: 'body',
        heading: d.heading || '',
        body: d.body || '',
        alignment: d.alignment,
      } as TextSlideData;
    }
    case 'split': {
      const d = data as SplitSlideData;
      return {
        template: 'text',
        layout: 'two-column',
        body: d.leftContent || '',
        secondaryBody: d.rightContent || '',
        ratio: d.ratio || '50-50',
      } as TextSlideData;
    }
    case 'image-text': {
      const d = data as ImageTextSlideData;
      return {
        template: 'text',
        layout: 'image-text',
        heading: d.heading || '',
        body: d.body || '',
        imageUrl: d.imageUrl,
        imagePosition: d.imagePosition,
        imageSize: d.imageSize,
      } as TextSlideData;
    }
    case 'media': {
      const d = data as MediaSlideData;
      if (d.mediaType === 'video') {
        return {
          template: 'video',
          url: d.url || '',
          caption: d.caption,
        } as VideoSlideData;
      }
      return {
        template: 'image',
        url: d.url || '',
        caption: d.caption,
        overlay: d.overlay,
      } as ImageSlideData;
    }
    default:
      // Non-text types (image, video, quiz, webapp, code, canva, text) pass through
      return data;
  }
}

// createSlide moved to './slideRegistry' — defaults come from the registry now.

// Template metadata for UI
export type TemplateCategory = 'content' | 'interactive' | 'embed';

export interface TemplateInfo {
  id: SlideTemplate;
  /** Stable registry key (e.g. 'text-title', 'activity-likert') — used by the
   *  picker for wireframe thumbnails and the frequently-used row. */
  key: string;
  name: string;
  description: string;
  icon: string; // Lucide icon name
  layout?: TextSlideLayout; // For text slide layout presets
  activityType?: ActivityType; // For activity slide sub-variants
  category?: TemplateCategory;
  /** Tenant entitlement flag gating visibility in the picker (e.g. 'attune'). */
  requiredFeature?: string;
}

// templateInfo + SUBMITTABLE_TEMPLATES are now derived from SLIDE_REGISTRY.
// Import them from './slideRegistry' instead of './slideTypes'.
